/**
 * Persistent binding badge for Engine / Launch Calendar scenario chrome.
 * Answers: does this scenario tie back to a cycle, or is it unbound what-if?
 */

import { useEffect, useState } from 'react';
import { getPlanCycleById } from '../../lib/planningService';
import { isPlanCycleCommitTarget, type PlanCycleStatus } from '../../types/planning';
import type { Scenario } from '../../lib/planningEngineModule.types';

/**
 * Badge: "What-if · unbound" vs "Cycle: <name>" (with status when the cycle
 * can no longer receive commits — simulation stays open either way).
 * Resolves cycle name asynchronously when planCycleId is set.
 */
export function ScenarioBindingBadge({
  scenario,
  className = '',
}: {
  scenario: Pick<Scenario, 'planCycleId'> | null | undefined;
  className?: string;
}) {
  const planCycleId = scenario?.planCycleId ?? null;
  const [cycle, setCycle] = useState<{ name: string; status: PlanCycleStatus } | null>(null);

  useEffect(() => {
    if (!planCycleId) {
      setCycle(null);
      return;
    }
    let cancelled = false;
    void getPlanCycleById(planCycleId).then(({ data }) => {
      if (!cancelled) setCycle(data ? { name: data.name, status: data.status } : null);
    });
    return () => {
      cancelled = true;
    };
  }, [planCycleId]);

  if (!scenario) return null;

  if (!planCycleId) {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5  border text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)] dark:border-[var(--mustard)] ${className}`}
        title="Cycle binding — this scenario is exploratory (not linked to a Plan Cycle). This is not Input Scope."
      >
        What-if · no cycle
      </span>
    );
  }

  const closed = cycle != null && !isPlanCycleCommitTarget(cycle.status);
  if (closed) {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5  border text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)] dark:bg-[var(--surface-raised)] ${className}`}
        title={`Cycle binding — this cycle is ${cycle.status}. Simulation stays open, but commits into it are blocked.`}
      >
        Cycle: {cycle.name.trim() || '…'} · {cycle.status}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5  border text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)] ${className}`}
      title="Cycle binding — this scenario is linked to a Plan Cycle. This is not Input Scope."
    >
      Cycle: {cycle?.name.trim() || '…'}
    </span>
  );
}
